import axios from "axios";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import LoginFun from "../../Layout Component/Loginfun";
import { useContextMenu } from "../../Layout Component/ContextMenu";
import { setLoader } from "../../Store/Reducers/UserReducer";
import MedicalHistoryNav from "./MedicalHistoryNav";
import { displayValue, isAnswered, sectionsFor, visibleFields } from "./questionnaireFields";

// ── Medical History & Questionnaire ──────────────────────────────────────────
//
// Laid out like the clinical OX.ar page of the same name:
//
//   VIEW  — "66 of 103 answered", a switch for the unanswered ones, and the
//           sections as read-only cards in columns, each with its own Edit and
//           an answered count. Click a section (or right-click it) to edit it.
//   EDIT  — the questions of the chosen section(s) as a two-column form, with
//           Save and Cancel. "Edit all" opens every section.
//
// The questions are data (questionnaireFields.js); the record, the API calls
// and the save are the same ones the old two-step form used.

const url = process.env.REACT_APP_OXAR_API_URL;

const parse = (key) => {
  try { return JSON.parse(key === "UData" ? localStorage.getItem(key) : sessionStorage.getItem(key)); } catch { return null; }
};

const blank = (v) => v === null || v === undefined || String(v).trim() === "" || ["n/a", "null"].includes(String(v).trim().toLowerCase());
const firstOf = (obj, keys) => {
  for (const k of keys) if (!blank(obj?.[k])) return obj[k];
  return null;
};
const dateText = (raw) => {
  if (blank(raw) || String(raw).startsWith("0001-01-01")) return null;
  const m = moment(raw);
  return m.isValid() ? m.format("DD/MM/YYYY") : null;
};

// Read-only sections built from the profile and the partner record, which are
// edited on their own pages.
function profileSections(profile, partner) {
  const p = profile || {};
  const r = partner || {};
  return [
    {
      id: "A", title: "Section A: Personal details", editTo: "/ProfileData", editLabel: "Edit profile",
      rows: [
        ["First name", p.firstName],
        ["Last name", p.lastName],
        ["Date of birth", dateText(p.birthday)],
        ["E-mail address", p.email],
        ["What was your sex assigned at birth?", p.gendercodeName],
        ["How old are you?", p.age],
        ["Mobile telephone number", p.mobileNumber],
        ["What is your job?", p.occupation],
        ["Previous surname", p.previousName],
        ["Preferred name", p.preferredName],
      ],
    },
    {
      id: "B", title: "Section B: Partner details", editTo: "/PartnerDetails", editLabel: "View partner",
      rows: [
        ["Partner name", firstOf(r, ["fullname", "fullName"])],
        ["Partner date of birth", dateText(firstOf(r, ["birthdate", "birthday"]))],
        ["Partner sex assigned at birth", firstOf(r, ["gendercodeName", "gender"])],
        ["Partner e-mail address", firstOf(r, ["emailaddress1", "email"])],
        ["Partner mobile telephone number", firstOf(r, ["mobilephone", "mobileNumber"])],
      ],
    },
    {
      id: "C", title: "Section C: Previous pregnancies", editTo: "/PreviousPregnancy", editLabel: "Open",
      rows: [["Previous pregnancies", "Recorded in the Previous Pregnancy section"]],
      linkOnly: true,
    },
  ];
}

function Field({ field, value, onChange }) {
  const id = `q-${field.name}`;
  if (field.type === "yn") {
    return (
      <select id={id} className="form-select" value={isAnswered(field, value) ? String(value) : ""} onChange={(e) => onChange(field, e.target.value === "" ? null : Number(e.target.value))}>
        <option value="">— Select —</option>
        <option value="271400001">No</option>
        <option value="271400000">Yes</option>
      </select>
    );
  }
  if (field.type === "select") {
    return (
      <select id={id} className="form-select" value={isAnswered(field, value) ? String(value) : ""} onChange={(e) => onChange(field, e.target.value === "" ? null : Number(e.target.value))}>
        <option value="">— Select —</option>
        {field.options.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
      </select>
    );
  }
  if (field.type === "number") {
    return (
      <div className="ox-field__num">
        <input id={id} type="number" className="form-control" step={field.step || "1"} min={field.min} max={field.max}
          value={value ?? ""} onChange={(e) => onChange(field, e.target.value === "" ? null : Number(e.target.value))} />
        {field.unit && <span className="ox-field__unit">{field.unit}</span>}
      </div>
    );
  }
  if (field.type === "date") {
    const v = blank(value) || String(value).startsWith("0001") ? "" : String(value).substring(0, 10);
    return <input id={id} type="date" className="form-control" value={v} onChange={(e) => onChange(field, e.target.value || null)} />;
  }
  return <input id={id} type="text" className="form-control" value={value ?? ""} onChange={(e) => onChange(field, e.target.value)} />;
}

export default function MedicalHistory() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state: routeState } = useLocation();
  const logindata = LoginFun();
  const { show, menu } = useContextMenu();

  const gender = String(localStorage.getItem("PatientGender")) === "1" ? "male" : "female";
  const profile = parse("UData");
  const partner = parse("ReleatedcontactData");

  const [record, setRecord] = useState(null);
  const [saved, setSaved] = useState(null);       // last saved copy, for Cancel and "unsaved"
  // Opened straight into editing when the patient came from "Edit" on the overview.
  const [editing, setEditing] = useState(routeState?.edit || null);   // null | "all" | section id
  const [showUnanswered, setShowUnanswered] = useState(true);
  const [saving, setSaving] = useState(false);

  const sessionCheck = useCallback(() => {
    const now = Math.round(new Date().getTime() / 1000);
    const expires = localStorage.getItem("ExpirretokenTime");
    if (expires < now) {
      dispatch(setLoader(false));
      Swal.fire({ title: "Session Expired", text: "Your session has been expired! Please login again", icon: "warning", confirmButtonText: "Okay" })
        .then(() => logindata.logout());
      return false;
    }
    return true;
  }, [dispatch, logindata]);

  const load = useCallback(async () => {
    try {
      dispatch(setLoader(true));
      const id = localStorage.getItem("UserID");
      const res = await axios(`${url}api/Profile/GetQuestionairebyPatientId?id=${id}`);
      const c = res.data.content || {};
      ["whatDateWasYourLastMenstrualPeriod", "whenWasYourLastSmearTest"].forEach((k) => {
        const d = c[k] ? String(c[k]).substring(0, 10) : "";
        c[k] = d === "0001-01-01" ? "" : d;
      });
      setRecord(c);
      setSaved(c);
    } catch {
      toast.error("Your medical history could not be loaded. Please refresh to try again.");
    } finally {
      dispatch(setLoader(false));
    }
  }, [dispatch]);

  useEffect(() => {
    sessionCheck();
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sections = useMemo(() => sectionsFor(gender), [gender]);
  const readOnly = useMemo(() => profileSections(profile, partner), []); // eslint-disable-line react-hooks/exhaustive-deps

  // "66 of 103 answered" counts the questionnaire's own questions that apply.
  const counts = useMemo(() => {
    const per = {};
    let answered = 0;
    let total = 0;
    sections.forEach((s) => {
      const fs = visibleFields(s, record, gender);
      const a = fs.filter((f) => isAnswered(f, record?.[f.name])).length;
      per[s.id + s.title] = [a, fs.length];
      answered += a;
      total += fs.length;
    });
    return { per, answered, total };
  }, [sections, record, gender]);

  const dirty = editing !== null && JSON.stringify(record) !== JSON.stringify(saved);

  const change = (field, value) => setRecord((r) => ({ ...r, [field.name]: value }));

  const cancel = () => { setRecord(saved); setEditing(null); };

  const save = async () => {
    if (!sessionCheck()) return;
    if (record?.heightinMetres > 3 || record?.heightinMetres < 0) {
      toast.warn("Height should be between 0 and 3 metres");
      return;
    }
    try {
      setSaving(true);
      const id = localStorage.getItem("UserID");
      await axios.put(`${url}/api/Profile/UpdateQuestionaire?userId=${id}`, record);
      setSaved(record);
      setEditing(null);
      toast.success("Your medical history has been saved", { autoClose: 3000, hideProgressBar: true });
    } catch (error) {
      const invalid = error?.response?.data?.errorMessage === "Invalid json";
      toast.error(invalid ? "Please don't use special characters in fields" : "Error while updating");
    } finally {
      setSaving(false);
    }
  };

  const editSection = (id) => setEditing((cur) => (cur === "all" ? "all" : id));

  const sectionMenu = (e, s) =>
    show(e, [
      { label: s ? `Edit ${s.title.split(":")[0]}` : "Edit", onClick: () => editSection(s.id) },
      { label: "Edit all sections", onClick: () => setEditing("all") },
      { divider: true },
      { label: "Print", onClick: () => window.print() },
      { label: "Refresh", onClick: load },
    ]);

  const isEditingSection = (s) => editing === "all" || editing === s.id;

  const personLine = profile
    ? [profile.firstName, profile.lastName].filter(Boolean).join(" ")
    : "";

  return (
    <div className="ox-mh" onContextMenu={(e) => show(e, [
      { label: "Edit all sections", onClick: () => setEditing("all") },
      { label: "Print", onClick: () => window.print() },
      { label: "Refresh", onClick: load },
    ])}>
      <MedicalHistoryNav />

      <div className="ox-mh__bar">
        <div>
          <span className="ox-hint">Medical History &amp; Questionnaire</span>
          <h2 className="ox-mh__heading">Medical History <small>{gender} form{personLine ? ` · ${personLine}` : ""}</small></h2>
        </div>
        <div className="ox-mh__actions">
          {editing === null ? (
            <>
              <button type="button" className="ox-btn" onClick={() => setEditing("all")}>Edit all</button>
              <button type="button" className="ox-btn" onClick={() => window.print()}>Print</button>
              <button type="button" className="ox-btn" onClick={load}>Refresh</button>
            </>
          ) : (
            <>
              <button type="button" className="ox-btn" onClick={cancel} disabled={saving}>Cancel</button>
              <button type="button" className="ox-btn ox-btn--primary" onClick={save} disabled={saving || !dirty}>
                {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="ox-mh__summary">
        <span><strong>{counts.answered}</strong> of {counts.total} answered</span>
        <label className="ox-check">
          <input type="checkbox" checked={showUnanswered} onChange={(e) => setShowUnanswered(e.target.checked)} />
          Show the unanswered ones
        </label>
        <span className="ox-hint">{editing === null ? "Click a section to edit it. Right-click for more." : "Editing — your changes are saved when you press Save."}</span>
      </div>

      <div className="ox-mh__cols">
        {/* Profile-backed sections: read-only here, edited on their own pages. */}
        {readOnly.map((s) => {
          const rows = s.rows.filter(([, v]) => !blank(v) || (showUnanswered && !s.linkOnly));
          const answered = s.rows.filter(([, v]) => !blank(v)).length;
          return (
            <section
              key={s.id}
              className="ox-card ox-clickable"
              tabIndex={0}
              onClick={() => navigate(s.editTo)}
              onKeyDown={(e) => e.key === "Enter" && navigate(s.editTo)}
              onContextMenu={(e) => show(e, [{ label: s.editLabel, onClick: () => navigate(s.editTo) }, { divider: true }, { label: "Print", onClick: () => window.print() }])}
            >
              <header className="ox-card__head">
                <h3>{s.title}</h3>
                <span className="ox-card__count">{s.linkOnly ? "" : `${answered}/${s.rows.length}`}</span>
                <button type="button" className="ox-btn ox-btn--sm" onClick={(e) => { e.stopPropagation(); navigate(s.editTo); }}>{s.editLabel === "Edit profile" ? "Edit" : s.editLabel}</button>
              </header>
              <dl className="ox-card__rows">
                {rows.map(([label, v]) => (
                  <div key={label} className={blank(v) ? "is-empty" : ""}><dt>{label}</dt><dd>{blank(v) ? "—" : String(v)}</dd></div>
                ))}
              </dl>
            </section>
          );
        })}

        {sections.map((s) => {
          const fs = visibleFields(s, record, gender);
          const [a, t] = counts.per[s.id + s.title] || [0, fs.length];
          const open = isEditingSection(s);
          const shown = open || showUnanswered ? fs : fs.filter((f) => isAnswered(f, record?.[f.name]));
          return (
            <section
              key={s.id + s.title}
              className={`ox-card${open ? " is-editing" : " ox-clickable"}`}
              tabIndex={open ? -1 : 0}
              onClick={() => { if (!open && editing === null) editSection(s.id); }}
              onKeyDown={(e) => { if (!open && e.key === "Enter") editSection(s.id); }}
              onContextMenu={(e) => sectionMenu(e, s)}
            >
              <header className="ox-card__head">
                <h3>{s.title}</h3>
                <span className="ox-card__count">{a}/{t}</span>
                {!open && <button type="button" className="ox-btn ox-btn--sm" onClick={(e) => { e.stopPropagation(); editSection(s.id); }}>Edit</button>}
              </header>

              {open ? (
                <div className="ox-form">
                  {fs.map((f) => (
                    <div key={f.name} className={`ox-form__row${f.follows ? " is-followup" : ""}`}>
                      <label htmlFor={`q-${f.name}`}>{f.label}</label>
                      <Field field={f} value={record?.[f.name]} onChange={change} />
                    </div>
                  ))}
                  {s.id === "F" && (
                    <p className="ox-hint">Your allergies are listed on the <button type="button" className="ox-link" onClick={() => navigate("/Allergies")}>Allergies</button> page.</p>
                  )}
                </div>
              ) : (
                <dl className="ox-card__rows">
                  {shown.map((f) => {
                    const v = displayValue(f, record?.[f.name]);
                    return (
                      <div key={f.name} className={v === null ? "is-empty" : ""}>
                        <dt>{f.label}</dt>
                        <dd className={f.type === "yn" ? (v === "Yes" ? "is-yes" : "is-no") : ""}>
                          {v === null ? "—" : f.type === "yn" ? (v === "Yes" ? "✓ Yes" : "✗ No") : v}
                        </dd>
                      </div>
                    );
                  })}
                  {shown.length === 0 && <p className="ox-hint">Nothing answered in this section yet.</p>}
                </dl>
              )}
            </section>
          );
        })}
      </div>

      {editing !== null && (
        <div className="ox-mh__savebar" role="region" aria-label="Save changes">
          <span className="ox-hint">{dirty ? "You have unsaved changes." : "No changes yet."}</span>
          <button type="button" className="ox-btn" onClick={cancel} disabled={saving}>Cancel</button>
          <button type="button" className="ox-btn ox-btn--primary" onClick={save} disabled={saving || !dirty}>{saving ? "Saving…" : "Save changes"}</button>
        </div>
      )}
      {menu}
    </div>
  );
}
